import React, { useMemo } from 'react';
import DashboardDataTable from '../dashboard/DashboardDataTable';
import { EmptyState } from '../dashboard/states';
import Badge from './Badge';
import { fmtDateTime, fmtDuration, fmtNumber, fmtPct } from './format';

function pctOf(num, den) {
  return den > 0 ? (num / den) * 100 : null;
}

const KIND_LABELS = { plan: 'plan', dispatch: 'dispatch', send: 'send' };

/**
 * Pipeline runs (messaging.pipeline_runs). Each row is one execution of the
 * export → clean → dedupe → send pipeline. Click a row for the full funnel,
 * template split and per-row failures.
 */
export default function RunsTable({ runs = [], loading, onSelect }) {
  const columns = useMemo(
    () => [
      {
        key: 'startedAt',
        header: 'Started (PT)',
        sortable: true,
        sortAccessor: (r) => new Date(r.startedAt || 0).getTime(),
        cell: (r) => (
          <div>
            <div className="font-medium text-zinc-900">{fmtDateTime(r.startedAt)}</div>
            <div className="truncate font-mono text-[10px] text-zinc-400" title={r.runId}>
              {r.runId}
            </div>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        cell: (r) => (
          <Badge state={r.status} pulse={r.status === 'running'}>
            {r.status}
          </Badge>
        ),
      },
      {
        key: 'kind',
        header: 'Kind',
        sortable: true,
        sortAccessor: (r) => r.kind || 'send',
        cell: (r) => <Badge tone={r.kind === 'plan' ? 'indigo' : r.kind === 'dispatch' ? 'emerald' : 'zinc'}>{KIND_LABELS[r.kind] || 'send'}</Badge>,
      },
      {
        key: 'mode',
        header: 'Mode',
        cell: (r) =>
          r.kind === 'plan' ? (
            <span className="text-[10px] text-zinc-400">n/a</span>
          ) : (
            <Badge tone={r.mode === 'live' ? 'rose' : 'sky'}>{r.mode === 'live' ? 'LIVE' : 'dry run'}</Badge>
          ),
      },
      {
        key: 'trigger',
        header: 'Trigger',
        cell: (r) => (
          <span className="text-zinc-600">
            {r.trigger || '—'}
            {r.triggeredBy ? <span className="block text-[10px] text-zinc-400">{String(r.triggeredBy).replace(/^admin:/, '')}</span> : null}
          </span>
        ),
      },
      {
        key: 'window',
        header: 'Arrival window',
        cell: (r) => (r.window?.start ? `${r.window.start} → ${r.window.end}` : '—'),
      },
      {
        key: 'exported',
        header: 'Exported',
        align: 'right',
        sortable: true,
        sortAccessor: (r) => r.funnel?.exported || 0,
        cell: (r) => fmtNumber(r.funnel?.exported || 0),
      },
      {
        key: 'ready',
        header: 'Ready',
        align: 'right',
        sortable: true,
        sortAccessor: (r) => r.funnel?.ready || 0,
        cell: (r) => fmtNumber(r.funnel?.ready ?? r.funnel?.selectedRows ?? 0),
      },
      {
        key: 'sent',
        header: 'Sent / planned',
        align: 'right',
        sortable: true,
        sortAccessor: (r) => (r.kind === 'plan' ? r.summary?.planned || 0 : r.summary?.sent || 0),
        cell: (r) =>
          r.kind === 'plan' ? (
            <span className="font-semibold text-zinc-900">
              {fmtNumber(r.summary?.planned || 0)} <span className="text-[10px] font-normal text-zinc-400">touches</span>
            </span>
          ) : (
            <span className="font-semibold text-zinc-900">{fmtNumber(r.summary?.sent || 0)}</span>
          ),
      },
      {
        key: 'failed',
        header: 'Failed',
        align: 'right',
        sortable: true,
        sortAccessor: (r) => r.summary?.failed || 0,
        cell: (r) => {
          const failed = r.summary?.failed || 0;
          const rate = pctOf(failed, r.summary?.processed || 0);
          return failed ? (
            <span className="text-rose-700">
              {fmtNumber(failed)} <span className="text-[10px] text-rose-400">({fmtPct(rate, 0)})</span>
            </span>
          ) : (
            <span className="text-zinc-400">0</span>
          );
        },
      },
      {
        key: 'skipped',
        header: 'Skipped',
        align: 'right',
        cell: (r) => (
          <span className="text-zinc-500">
            {fmtNumber(
              r.kind === 'plan'
                ? r.summary?.guestsSkipped || 0
                : r.kind === 'dispatch'
                ? (r.summary?.skippedAlreadyOrdered || 0) + (r.summary?.skippedAlreadyTexted || 0) + (r.summary?.skippedInvalidPhone || 0)
                : r.summary?.skipped || 0
            )}
          </span>
        ),
      },
      {
        key: 'durationMs',
        header: 'Duration',
        align: 'right',
        sortable: true,
        cell: (r) => fmtDuration(r.durationMs),
      },
    ],
    []
  );

  return (
    <div className="h-[420px]">
      {loading ? (
        <div className="h-full w-full animate-pulse rounded-lg border border-zinc-200 bg-zinc-50" />
      ) : (
        <DashboardDataTable
          rows={runs}
          columns={columns}
          onRowClick={onSelect}
          initialSort={{ key: 'startedAt', dir: 'desc' }}
          searchPlaceholder="Search run id, trigger, window…"
          emptyState={
            <EmptyState
              title="No pipeline runs recorded"
              description="Runs are recorded by the updated send script. Older sends still appear under Messages."
            />
          }
        />
      )}
    </div>
  );
}
